<template>
  <div class="leader">
    <div class="bg-decoration">
      <div class="cloud cloud-1"></div>
      <div class="cloud cloud-2"></div>
      <div class="wave wave-1"></div>
    </div>

    <div class="header">
      <button class="back-btn" @click="goBack">
        <i class="fa-solid fa-arrow-left"></i>
      </button>
      <h1>选择目的地</h1>
    </div>

    <div class="search-bar">
      <i class="fa-solid fa-magnifying-glass"></i>
      <input
        v-model="searchQuery"
        type="text"
        placeholder="搜索国家..."
      />
      <span v-if="searchQuery" class="clear-btn" @click="searchQuery = ''">
        <i class="fa-solid fa-xmark"></i>
      </span>
    </div>

    <div class="flags">
      <div
        v-for="country in filteredCountries"
        :key="country.code"
        class="flag-card"
        @click="goToCountry(country.url)"
      >
        <div class="flag-bg"></div>
        <div class="flag-main">
          <div class="flag-emoji">{{ country.flag }}</div>
          <div class="flag-info">
            <span class="flag-name">{{ country.name }}</span>
            <span class="flag-code">{{ country.code }}</span>
          </div>
        </div>
        <div class="flag-arrow">
          <i class="fa-solid fa-arrow-right"></i>
        </div>
      </div>
    </div>

    <div v-if="filteredCountries.length === 0" class="no-result">
      <i class="fa-solid fa-search"></i>
      <p>未找到 "{{ searchQuery }}" 相关国家</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { useRouter } from "vue-router";
import { Country } from "@/config/types";
import countriesData from "@/config/countries.json";

export default defineComponent({
  name: "Leader",
  setup() {
    const router = useRouter();
    const searchQuery = ref("");
    const countries: Country[] = countriesData;

    const filteredCountries = computed(() => {
      if (!searchQuery.value) return countries;
      const query = searchQuery.value.toLowerCase();
      return countries.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.code.toLowerCase().includes(query)
      );
    });

    const goToCountry = (url: string) => {
      location.href = url;
    };
    const goBack = () => {
      router.push("/");
    };

    return { searchQuery, filteredCountries, goToCountry, goBack };
  },
});
</script>

<style scoped>
.leader {
  position: relative;
  min-height: 100vh;
  background: linear-gradient(180deg, #e8f4fc 0%, #d4e9f7 30%, #f0f6fa 70%, #fafcfd 100%);
  padding: 20px;
}

.bg-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.cloud {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.2) 100%);
}

.cloud-1 {
  top: 5%;
  left: 10%;
  width: 200px;
  height: 80px;
  animation: cloudFloat 12s ease-in-out infinite;
}

.cloud-2 {
  top: 12%;
  right: 8%;
  width: 140px;
  height: 50px;
  animation: cloudFloat 15s ease-in-out infinite reverse;
}

@keyframes cloudFloat {
  0%, 100% { transform: translateY(0) translateX(0); opacity: 0.6; }
  50% { transform: translateY(-8px) translateX(3px); opacity: 0.85; }
}

.wave {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 150px;
  background: linear-gradient(180deg, transparent 0%, rgba(200, 220, 240, 0.25) 100%);
}

.wave::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='%23c9a962' fill-opacity='0.12' d='M0,160L48,144C96,128,192,96,288,90.7C384,85,480,107,576,133.3C672,160,768,192,864,197.3C960,203,1056,181,1152,165.3C1248,149,1344,139,1392,133.3L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z'%3E%3C/path%3E%3C/svg%3E") no-repeat bottom;
  background-size: cover;
}

.header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(201, 169, 98, 0.3);
  border-radius: 12px;
  color: #4a7eb8;
  cursor: pointer;
  transition: all 0.3s;
}

.back-btn:hover {
  background: #4a7eb8;
  border-color: #4a7eb8;
  color: white;
  transform: translateX(-2px);
}

h1 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c4a6e;
  margin: 0;
  letter-spacing: 2px;
}

.search-bar {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(201, 169, 98, 0.3);
  border-radius: 14px;
  padding: 14px 18px;
  margin-bottom: 24px;
  transition: all 0.3s;
}

.search-bar:focus-within {
  border-color: rgba(201, 169, 98, 0.6);
  box-shadow: 0 4px 20px rgba(74, 126, 184, 0.1);
}

.search-bar i {
  color: #8ba4c4;
  font-size: 1rem;
}

.search-bar input {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: #2c4a6e;
  font-size: 1rem;
}

.search-bar input::placeholder {
  color: #8ba4c4;
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: rgba(74, 126, 184, 0.1);
  border-radius: 50%;
  color: #6b8caa;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-btn:hover {
  background: rgba(74, 126, 184, 0.2);
  color: #4a7eb8;
}

.flags {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding-bottom: 40px;
}

@media (min-width: 640px) {
  .flags {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1024px) {
  .flags {
    grid-template-columns: repeat(4, 1fr);
  }
}

.flag-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(201, 169, 98, 0.25);
  border-radius: 14px;
  padding: 16px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.flag-card:hover {
  transform: translateY(-4px);
  border-color: rgba(201, 169, 98, 0.5);
  box-shadow: 0 12px 28px rgba(74, 126, 184, 0.1), 0 0 16px rgba(201, 169, 98, 0.08);
}

.flag-bg {
  position: absolute;
  right: -20px;
  top: 50%;
  transform: translateY(-50%);
  width: 80px;
  height: 80px;
  background: radial-gradient(circle, rgba(201, 169, 98, 0.12) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.3s;
}

.flag-card:hover .flag-bg {
  opacity: 1;
}

.flag-main {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.flag-emoji {
  font-size: 2rem;
  flex-shrink: 0;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s;
}

.flag-card:hover .flag-emoji {
  transform: scale(1.15);
}

.flag-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.flag-name {
  font-size: 1rem;
  font-weight: 600;
  color: #2c4a6e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.flag-code {
  font-size: 0.75rem;
  color: #8ba4c4;
  letter-spacing: 1px;
}

.flag-arrow {
  color: #c9a962;
  font-size: 0.875rem;
  transition: all 0.3s;
  flex-shrink: 0;
}

.flag-card:hover .flag-arrow {
  transform: translateX(4px);
  color: #b8943f;
}

.no-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #8ba4c4;
}

.no-result i {
  font-size: 3rem;
  margin-bottom: 16px;
  opacity: 0.5;
}

.no-result p {
  font-size: 1rem;
  text-align: center;
}
</style>
